var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};

// src/lib/referrer.ts
var defaultReferrer = "https://start.loopbound.com";
var referrer = sessionStorage.getItem("lbReferrer");
var getReferrer = () => {
  if (referrer)
    return referrer;
  let url = new URL(location.href);
  referrer = url.searchParams.get("lbReferrer");
  if (!referrer)
    referrer = document.referrer;
  if (referrer)
    sessionStorage.setItem("lbReferrer", referrer);
  return referrer ?? defaultReferrer;
};

// src/lib/resolvablePromise.ts
var ResolvablePromise = class {
  constructor() {
    __publicField(this, "_resolve");
    __publicField(this, "_reject");
    __publicField(this, "_promise");
    __publicField(this, "_value");
    this._promise = new Promise((resolve, reject) => {
      this._resolve = resolve;
      this._reject = reject;
    });
  }
  get promise() {
    return this._promise;
  }
  get value() {
    return this._value;
  }
  cleanup() {
    this._resolve = void 0;
    this._reject = void 0;
  }
  resolve(value) {
    this._value = value;
    this._resolve(value);
    this._promise = Promise.resolve(value);
    this.cleanup();
  }
  reject(reason) {
    this._reject(reason);
    this._promise = Promise.reject(reason);
    this.cleanup();
  }
};

// src/state/shellProvider.ts
import React from "react";
var ShellContext = React.createContext(null);
var globalShellConfig = new ResolvablePromise();
var getGlobalShellConfig = () => globalShellConfig.promise;
var getGlobalShellConfigValue = () => globalShellConfig.value;
var ShellProvider = ({
  children,
  config
}) => {
  try {
    globalShellConfig.resolve(config);
  } catch (err) {
  }
  return React.createElement(ShellContext.Provider, { value: config }, children);
};
var useShell = () => React.useContext(ShellContext);

// src/state/auth/session.ts
import { ServiceError, internalServerError, isServiceError } from "@lb-lib/error";
import { Observer, useObserver } from "@lb-lib/observer";

// src/state/auth/sessionClient.ts
import {
  createSessionClient
} from "@loopbound-ares/rpc-id/clients/session";
var sessionClient = (async () => {
  let config = await getGlobalShellConfig();
  return () => createSessionClient({
    endpoint: config.services.idApi,
    client: {
      name: "shell",
      version: "latest"
    }
  });
})();
var withSessionClient = async (fn) => {
  let client = await sessionClient;
  return await fn(client());
};

// src/state/auth/authTicket.ts
var removeHash = () => {
  history.replaceState(null, document.title, location.pathname + location.search);
};
var checkAuthTicketPromise = (async () => {
  let hash = location.hash;
  let parsed = new URLSearchParams(hash.slice(1));
  if (parsed.has("__loopbound_auth_ticket")) {
    let ticket = parsed.get("__loopbound_auth_ticket");
    removeHash();
    await withSessionClient(
      (c) => c.handleAuthTicket({
        authTicket: ticket
      })
    );
  }
  let userId = null;
  if (parsed.has("__loopbound_user_id")) {
    userId = parsed.get("__loopbound_user_id");
    removeHash();
  }
  return {
    userId
  };
})();
var checkAuthTicket = async () => {
  return await checkAuthTicketPromise;
};

// src/state/auth/session.ts
var sessionObserver = new Observer();
sessionObserver.notify({
  state: "loading",
  user: null,
  token: null,
  deviceId: null,
  sessionUsers: []
});
var redirectToLogin = async () => {
  let shellConfig = await getGlobalShellConfig();
  let url = new URL(shellConfig.services.idHost);
  url.searchParams.set("nextUrl", window.location.origin);
  url.searchParams.set("purpose", "select");
  window.location.replace(url.toString());
};
var redirectToUserSelector = async (userId, opts) => {
  let shellConfig = await getGlobalShellConfig();
  let url = new URL(shellConfig.services.idHost);
  url.searchParams.set(
    "nextUrl",
    opts?.withFullPath ? window.location.href : window.location.origin
  );
  url.searchParams.set("purpose", "select");
  url.searchParams.set("userId", userId ?? "");
  window.location.href = url.toString();
};
var getSession = () => {
  checkAuthTicket().then(async ({ userId }) => {
    try {
      let tokenResponse = await withSessionClient((c) => c.token({}));
      if (userId && tokenResponse.userId != userId && tokenResponse.deviceLoggedInUserId != userId) {
        redirectToUserSelector(userId);
        return;
      }
      sessionObserver.notify({
        state: "authenticated",
        user: tokenResponse.user,
        token: tokenResponse.clientSecret,
        deviceId: tokenResponse.deviceId,
        sessionUsers: tokenResponse.allSessionUsers
      });
    } catch (err) {
      if (isServiceError(err)) {
        if (err.data.status == 401) {
          sessionObserver.notify({
            state: "unauthenticated",
            user: null,
            token: null,
            deviceId: null,
            sessionUsers: []
          });
          redirectToLogin();
          return;
        }
      }
      sessionObserver.notify({
        state: "error",
        user: null,
        token: null,
        deviceId: null,
        sessionUsers: [],
        error: isServiceError(err) ? err : new ServiceError(
          internalServerError({
            message: "Unable to connect to the server. Please try again later."
          })
        )
      });
    }
  });
};
getSession();
var reloadSession = () => getSession();
setInterval(getSession, 1e3 * 60 * 3);
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState == "visible")
    getSession();
});
var getToken = () => new Promise((resolve, reject) => {
  let unsub = sessionObserver.subscribeWithInitial((response) => {
    if (response.state == "error")
      return reject(response.error);
    if (response.state != "authenticated")
      return;
    resolve(response.token);
    setTimeout(() => unsub(), 1);
  });
});
var useSession = () => useObserver(sessionObserver);
var logout = async () => {
  await withSessionClient((c) => c.logout({}));
  redirectToUserSelector();
};
var getUserOptional = () => sessionObserver.lastValue?.user;
var lastProbe = 0;
var probeAuth = () => {
  let now = Date.now();
  if (now - lastProbe < 1e3)
    return;
  lastProbe = now;
  getSession();
};

// src/state/auth/getServiceUrl.ts
var joinPath = (path, ...paths) => {
  return path + paths.map((p) => p.replace(/^\/|\/$/g, "")).join("/");
};
var getServiceUrl = (service, path) => {
  let baseUrl = new URL(
    getGlobalShellConfigValue()?.services[`${service}FrontendUrl`] ?? "https://loopbound.com"
  );
  path = joinPath(baseUrl.pathname, path ?? "/");
  baseUrl.pathname = path;
  if (baseUrl.hostname == location.hostname)
    return path ?? "/";
  let user = getUserOptional();
  if (user)
    baseUrl.hash = `__loopbound_user_id=${user.id}`;
  return baseUrl.toString();
};

// src/state/billing/billingAccount.ts
import { createLoader } from "@lb-lib/ui-data";

// src/state/billing/billingClient.ts
import { isServiceError as isServiceError2 } from "@lb-lib/error";
import { createClient } from "@lb-lib/rpc/client.mjs";
var billingClient = (async () => {
  let config = await getGlobalShellConfig();
  return () => createClient({
    endpoint: config.services.coreApi.billingUrl,
    client: {
      name: config.product.id,
      version: "latest"
    },
    headers: {
      "loopbound-service": "billing",
      "loopbound-product": config.product.id
    },
    getHeaders: async () => {
      let token = await getToken();
      return {
        Authorization: `Bearer ${token}`
      };
    }
  });
})();
var withBillingClient = async (fn) => {
  try {
    let client = await billingClient;
    return await fn(client());
  } catch (e) {
    if (isServiceError2(e) && e.data.status == 401)
      probeAuth();
    throw e;
  }
};

// src/state/billing/billingAccount.ts
var billingAccountState = createLoader({
  name: "billingAccount",
  hash: (i) => `billingAccount:${i.organizationId}`,
  fetch: async (i) => (await billingClient)().billing.getBillingAccount(
    {},
    { query: { organizationId: i.organizationId } }
  ),
  mutators: {}
});
var useBillingAccount = (organizationId) => {
  return billingAccountState.use({ organizationId });
};

// src/state/billing/billingObject.ts
import { createLoader as createLoader2 } from "@lb-lib/ui-data";
var billingObjectState = createLoader2({
  name: "billingObject",
  hash: (i) => `billingObject:${i.organizationId}:${i.object.id}:${i.object.type}`,
  fetch: async (i) => (await billingClient)().billing.getBillingAccountSmall(
    { object: i.object },
    { query: { organizationId: i.organizationId } }
  ),
  mutators: {}
});
var useBillingObject = (input) => {
  return billingObjectState.use(input);
};

// src/state/billing/billingObjectEntitlements.ts
import { createLoader as createLoader4 } from "@lb-lib/ui-data";

// src/state/organization/instanceClient.ts
import { isServiceError as isServiceError3 } from "@lb-lib/error";
import { createClient as createClient2 } from "@lb-lib/rpc/client.mjs";
var instanceClient = (async () => {
  let config = await getGlobalShellConfig();
  return () => createClient2({
    endpoint: config.services.coreApi.instanceUrl,
    client: {
      name: config.product.id,
      version: "latest"
    },
    headers: {
      "loopbound-service": "instance",
      "loopbound-product": config.product.id
    },
    getHeaders: async () => {
      let token = await getToken();
      return {
        Authorization: `Bearer ${token}`
      };
    }
  });
})();
var withInstanceClient = async (fn) => {
  try {
    let client = await instanceClient;
    return await fn(client());
  } catch (e) {
    if (isServiceError3(e) && e.data.status == 401)
      probeAuth();
    throw e;
  }
};

// src/state/organization/organizationAlerts.ts
import { Resource, useListRetriever, useMutation } from "@lb-lib/ui-data";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";

// src/state/organization/organizationClient.ts
import { isServiceError as isServiceError4 } from "@lb-lib/error";
import { createClient as createClient3 } from "@lb-lib/rpc/client.mjs";
var organizationClient = (async () => {
  let config = await getGlobalShellConfig();
  return () => createClient3({
    endpoint: config.services.coreApi.organizationUrl,
    client: {
      name: config.product.id,
      version: "latest"
    },
    headers: {
      "loopbound-service": "organization",
      "loopbound-product": config.product.id
    },
    getHeaders: async () => {
      let token = await getToken();
      return {
        Authorization: `Bearer ${token}`
      };
    }
  });
})();
var withOrganizationClient = async (fn) => {
  try {
    let client = await organizationClient;
    return await fn(client());
  } catch (e) {
    if (isServiceError4(e) && e.data.status == 401)
      probeAuth();
    throw e;
  }
};

// src/state/organization/organizationAlerts.ts
var organizationAlerts = new Resource("organizationAlert").addRetriever(
  "list",
  async (input) => {
    return await withOrganizationClient(
      (c) => c.organization.alert.list(
        {
          cursor: input.cursor,
          ...input.filter,
          order: "desc"
        },
        { query: { organizationId: input.organizationId } }
      )
    );
  }
).addAction(
  "dismiss",
  async ({ organizationId, alertId }) => {
    return await withOrganizationClient(
      (c) => c.organization.alert.dismiss({ alertId }, { query: { organizationId } })
    );
  }
).build();
var useOrganizationAlerts = (organizationId) => useListRetriever(
  organizationAlerts,
  "list",
  useMemo(() => ({ organizationId }), [organizationId])
);
var useCurrentOrganizationAlerts = () => {
  let params = useParams();
  let organizationId = params.organizationId;
  return useOrganizationAlerts(organizationId);
};
var useDismissOrganizationAlert = () => {
  return useMutation(useState(() => organizationAlerts.actions("dismiss"))[0]);
};

// src/state/organization/organizationAuditLogs.ts
import { Resource as Resource2, useListRetriever as useListRetriever2 } from "@lb-lib/ui-data";
import { useMemo as useMemo2 } from "react";
import { useParams as useParams2 } from "react-router-dom";
var organizationAuditLogs = new Resource2("organizationAuditLogs").addRetriever(
  "list",
  async (input) => {
    return await withOrganizationClient(
      (c) => c.organization.auditLog.list(
        {
          cursor: input.cursor,
          ...input.filter,
          order: "desc"
        },
        { query: { organizationId: input.organizationId } }
      )
    );
  }
).build();
var useOrganizationAuditLogs = (organizationId, filter) => useListRetriever2(
  organizationAuditLogs,
  "list",
  useMemo2(() => ({ organizationId, filter }), [organizationId, filter])
);
var useCurrentOrganizationAuditLogs = (filter) => {
  let params = useParams2();
  let organizationId = params.organizationId;
  return useOrganizationAuditLogs(organizationId, filter);
};

// src/state/organization/organizationIntegrations.ts
import { Resource as Resource3, useListRetriever as useListRetriever3, useMutation as useMutation2 } from "@lb-lib/ui-data";
import { useMemo as useMemo3, useState as useState2 } from "react";
var organizationIntegrations = new Resource3(
  "organizationIntegration"
).addRetriever("list", async (input) => {
  return await withOrganizationClient(
    (c) => c.organization.integration.list({ query: { organizationId: input.organizationId } })
  );
}).addAction(
  "uninstall",
  async ({
    organizationId,
    integrationInstallationId
  }) => {
    return await withOrganizationClient(
      (c) => c.organization.integration.uninstall(
        { integrationInstallationId },
        { query: { organizationId } }
      )
    );
  }
).build();
var useOrganizationIntegrations = (organizationId) => useListRetriever3(
  organizationIntegrations,
  "list",
  useMemo3(() => organizationId ? { organizationId } : null, [organizationId])
);
var useUninstallOrganizationIntegration = () => {
  return useMutation2(useState2(() => organizationIntegrations.actions("uninstall"))[0]);
};

// src/state/organization/organizationInvites.ts
import { Resource as Resource5, useListRetriever as useListRetriever5, useMutation as useMutation4 } from "@lb-lib/ui-data";
import { useEffect, useMemo as useMemo5, useRef, useState as useState4 } from "react";
import { useParams as useParams4 } from "react-router-dom";

// src/state/organization/organizations.ts
import { notFoundError, ServiceError as ServiceError2 } from "@lb-lib/error";
import { Resource as Resource4, useItemRetriever, useListRetriever as useListRetriever4, useMutation as useMutation3 } from "@lb-lib/ui-data";
import { useMemo as useMemo4, useState as useState3 } from "react";
import { useParams as useParams3 } from "react-router-dom";
var organizations = new Resource4("organizations").addRetriever("getAll", async () => {
  return await withOrganizationClient((c) => c.organization.getAll({}));
}).addRetriever("get", async ({ organizationId }) => {
  return await withOrganizationClient(
    (c) => c.organization.get({}, { query: { organizationId } })
  );
}).addAction("create", async ({ name }) => {
  return await withOrganizationClient((c) => c.organization.create({ name }));
}).addAction(
  "update",
  async ({
    organizationId,
    name,
    slug,
    imageFile
  }) => {
    return await withOrganizationClient(
      (c) => c.organization.update({ name, slug, imageFile }, { query: { organizationId } })
    );
  }
).build();
var useOrganizations = () => useListRetriever4(organizations, "getAll");
var useOrganization = (organizationId) => useItemRetriever(
  organizations,
  "get",
  useMemo4(() => ({ organizationId }), [organizationId])
);
var useCurrentOrganization = () => {
  let organizations2 = useOrganizations();
  let { organizationId, workspaceId, instanceId, projectId } = useParams3();
  let isLoading = organizations2.isLoading;
  if (!organizationId && !workspaceId && !instanceId && !projectId)
    isLoading = true;
  let organization = useMemo4(
    () => organizations2.data.find(
      (o) => o.organization.id == organizationId || o.organization.slug == organizationId || o.workspaces.some((w) => w.id == workspaceId || w.slug == workspaceId) || o.projects.some((p) => p.id == projectId || p.slug == projectId) || o.projects.some(
        (p) => p.instances.some((i) => i.id == instanceId || i.slug == instanceId)
      )
    ),
    [organizations2.data, organizationId, workspaceId, instanceId, projectId]
  );
  let error = organizations2.error;
  if (!isLoading && !error && !organization) {
    error = new ServiceError2(notFoundError({ entity: "organization" }));
  }
  return {
    ...organizations2,
    error,
    isLoading,
    data: organization,
    items: void 0
  };
};
var useCreateOrganization = () => {
  return useMutation3(useState3(() => organizations.actions("create"))[0]);
};
var useUpdateOrganization = () => {
  return useMutation3(useState3(() => organizations.actions("update"))[0]);
};
organizations.retrievers("getAll").load({ args: [] });

// src/state/organization/organizationInvites.ts
var organizationInvites = new Resource5("organizationInvites").addRetriever(
  "list",
  async ({ organizationId, cursor }) => {
    return await withOrganizationClient(
      (c) => c.organization.invite.list({ cursor, order: "desc" }, { query: { organizationId } })
    );
  }
).addAction(
  "remove",
  async ({ organizationId, inviteId }) => {
    return await withOrganizationClient(
      (c) => c.organization.invite.remove({ inviteId }, { query: { organizationId } })
    );
  }
).addAction(
  "createEmailInvite",
  async ({
    organizationId,
    email,
    role,
    message
  }) => {
    return await withOrganizationClient(
      (c) => c.organization.invite.createEmailInvite(
        { role, email, message },
        { query: { organizationId } }
      )
    );
  }
).addAction("createLinkInvite", async ({ organizationId }) => {
  return await withOrganizationClient(
    (c) => c.organization.invite.createLinkInvite({}, { query: { organizationId } })
  );
}).build();
var useOrganizationInvites = (organizationId, filter) => useListRetriever5(
  organizationInvites,
  "list",
  useMemo5(() => ({ organizationId, filter }), [organizationId, filter])
);
var useCurrentOrganizationInvites = (filter) => {
  let params = useParams4();
  let organizationId = params.organizationId;
  return useOrganizationInvites(organizationId, filter);
};
var useCreateOrganizationInvite = () => {
  return useMutation4(useState4(() => organizationInvites.actions("createEmailInvite"))[0]);
};
var useRemoveOrganizationInvite = () => {
  return useMutation4(useState4(() => organizationInvites.actions("remove"))[0]);
};
var useOrganizationInviteLink = () => {
  let org = useCurrentOrganization();
  let mutation = useMutation4(
    useState4(() => organizationInvites.actions("createLinkInvite"))[0]
  );
  let loadedInviteLinkRef = useRef(false);
  useEffect(() => {
    if (!loadedInviteLinkRef.current && org.data?.organization.id && org.data.member.role == "admin") {
      loadedInviteLinkRef.current = true;
      mutation.mutate({ organizationId: org.data?.organization.id });
    }
  }, [org.data?.organization.id]);
  return {
    linkInvite: mutation.data
  };
};

// src/state/organization/organizationMembers.ts
import { Resource as Resource6, useItemRetriever as useItemRetriever2, useListRetriever as useListRetriever6, useMutation as useMutation5 } from "@lb-lib/ui-data";
import { useMemo as useMemo6, useState as useState5 } from "react";
import { useParams as useParams5 } from "react-router-dom";
var organizationMembers = new Resource6("organizationMembers").addRetriever(
  "list",
  async (input) => {
    return await withOrganizationClient(
      (c) => c.organization.member.list(
        {
          cursor: input.cursor,
          ...input.filter,
          order: "desc"
        },
        { query: { organizationId: input.organizationId } }
      )
    );
  }
).addRetriever(
  "get",
  async ({ organizationId, memberId }) => {
    return await withOrganizationClient(
      (c) => c.organization.member.get({ memberId }, { query: { organizationId } })
    );
  }
).addAction(
  "remove",
  async ({ organizationId, memberId }) => {
    return await withOrganizationClient(
      (c) => c.organization.member.remove({ memberId }, { query: { organizationId } })
    );
  }
).addAction(
  "update",
  async ({
    organizationId,
    memberId,
    role
  }) => {
    return await withOrganizationClient(
      (c) => c.organization.member.update({ role, memberId }, { query: { organizationId } })
    );
  }
).build();
var useOrganizationMembers = (organizationId, filter) => useListRetriever6(
  organizationMembers,
  "list",
  useMemo6(() => ({ organizationId, filter }), [organizationId, filter])
);
var useOrganizationMember = (opts) => useItemRetriever2(
  organizationMembers,
  "get",
  useMemo6(() => opts, [opts?.organizationId, opts?.memberId])
);
var useCurrentOrganizationMembers = (filter) => {
  let params = useParams5();
  let organizationId = params.organizationId;
  return useOrganizationMembers(organizationId, filter);
};
var useUpdateOrganizationMember = () => {
  return useMutation5(useState5(() => organizationMembers.actions("update"))[0]);
};
var useRemoveOrganizationMember = () => {
  return useMutation5(useState5(() => organizationMembers.actions("remove"))[0]);
};

// src/state/organization/organizationNotifications.ts
import { Resource as Resource7, useListRetriever as useListRetriever7, useMutation as useMutation6 } from "@lb-lib/ui-data";
import { useMemo as useMemo7, useState as useState6 } from "react";
var organizationNotifications = new Resource7(
  "organizationNotification"
).addRetriever(
  "list",
  async (input) => {
    return await withOrganizationClient(
      (c) => c.organization.notification.list(
        {
          cursor: input.cursor,
          ...input.filter,
          order: "desc"
        },
        { query: { organizationId: input.organizationId } }
      )
    );
  }
).addAction(
  "read",
  async ({
    organizationId,
    notificationId
  }) => {
    return await withOrganizationClient(
      (c) => c.organization.notification.read({ notificationId }, { query: { organizationId } })
    );
  }
).addAction(
  "archive",
  async ({
    organizationId,
    notificationId
  }) => {
    return await withOrganizationClient(
      (c) => c.organization.notification.archive({ notificationId }, { query: { organizationId } })
    );
  }
).addAction(
  "unarchive",
  async ({
    organizationId,
    notificationId
  }) => {
    return await withOrganizationClient(
      (c) => c.organization.notification.unarchive(
        { notificationId },
        { query: { organizationId } }
      )
    );
  }
).build();
var useOrganizationNotifications = (organizationId, filter) => useListRetriever7(
  organizationNotifications,
  "list",
  useMemo7(
    () => organizationId ? { organizationId, filter } : null,
    [organizationId, filter]
  )
);
var useReadOrganizationNotification = () => {
  return useMutation6(useState6(() => organizationNotifications.actions("read"))[0]);
};
var useArchiveOrganizationNotification = () => {
  return useMutation6(useState6(() => organizationNotifications.actions("archive"))[0]);
};
var useUnarchiveOrganizationNotification = () => {
  return useMutation6(useState6(() => organizationNotifications.actions("unarchive"))[0]);
};

// src/state/organization/support.ts
import { Resource as Resource8, useItemRetriever as useItemRetriever3, useListRetriever as useListRetriever8, useMutation as useMutation7 } from "@lb-lib/ui-data";
import { useMemo as useMemo8, useState as useState7 } from "react";
var supportTickets = new Resource8("supportTickets").addRetriever("list", async ({ organizationId }) => {
  return await withOrganizationClient(
    (c) => c.organization.support.listTickets({}, { query: { organizationId } })
  );
}).addRetriever(
  "get",
  async ({ organizationId, ticketId }) => {
    return await withOrganizationClient(
      (c) => c.organization.support.getTicket({ ticketId }, { query: { organizationId } })
    );
  }
).addAction(
  "create",
  async ({
    organizationId,
    message,
    subject,
    url
  }) => {
    return await withOrganizationClient(
      (c) => c.organization.support.createTicket(
        { message, subject, url },
        { query: { organizationId } }
      )
    );
  }
).build();
var useSupportTickets = (organizationId) => useListRetriever8(
  supportTickets,
  "list",
  useMemo8(() => ({ organizationId }), [organizationId])
);
var useSupportTicket = ({
  organizationId,
  ticketId
}) => useItemRetriever3(
  supportTickets,
  "get",
  useMemo8(() => ({ organizationId, ticketId }), [organizationId, ticketId])
);
var useCreateSupportTicket = () => {
  return useMutation7(useState7(() => supportTickets.actions("create"))[0]);
};
var supportTicketMessages = new Resource8("supportTickets").addRetriever(
  "list",
  async ({ organizationId, ticketId }) => {
    return await withOrganizationClient(
      (c) => c.organization.support.listMessages({ ticketId }, { query: { organizationId } })
    );
  }
).addAction(
  "createMessage",
  async ({
    organizationId,
    ticketId,
    message
  }) => {
    await withOrganizationClient(
      (c) => c.organization.support.createMessage(
        { message, ticketId },
        { query: { organizationId } }
      )
    );
    return [];
  }
).addAction(
  "createAttachment",
  async ({
    organizationId,
    ticketId,
    file
  }) => {
    let form = new FormData();
    form.append("file", file);
    form.append("upload_preset", "support-files");
    let cloudinaryRes = await fetch(
      "https://api.cloudinary.com/v1_1/dqaqckwio/image/upload",
      {
        method: "POST",
        body: form
      }
    );
    let { secure_url } = await cloudinaryRes.json();
    await withOrganizationClient(
      (c) => c.organization.support.createAttachment(
        {
          ticketId,
          attachment: {
            name: file.name,
            url: secure_url
          }
        },
        { query: { organizationId } }
      )
    );
    return [];
  }
).build();
var useSupportTicketMessages = ({
  organizationId,
  ticketId
}) => useListRetriever8(
  supportTicketMessages,
  "list",
  useMemo8(() => ({ organizationId, ticketId }), [organizationId, ticketId])
);
var useCreateSupportTicketMessage = () => {
  return useMutation7(useState7(() => supportTicketMessages.actions("createMessage"))[0]);
};
var useCreateSupportTicketAttachment = () => {
  return useMutation7(useState7(() => supportTicketMessages.actions("createAttachment"))[0]);
};

// src/state/organization/workspaces.ts
import { ServiceError as ServiceError3, notFoundError as notFoundError2 } from "@lb-lib/error";
import { Resource as Resource9, createLoader as createLoader3, useListRetriever as useListRetriever9, useMutation as useMutation8 } from "@lb-lib/ui-data";
import { useEffect as useEffect2, useMemo as useMemo9, useState as useState8 } from "react";
import { useParams as useParams6 } from "react-router-dom";
import { toast } from "sonner";
var workspaces = new Resource9("workspaces").addRetriever("getAll", async () => {
  let retriever = organizations.retrievers("getAll");
  retriever.load({ args: [], force: true });
  let orgs = await retriever.getPromise();
  return orgs.flatMap((i) => i.workspaces);
}).addAction(
  "create",
  async ({ name, organizationId }) => {
    return await withOrganizationClient(
      (c) => c.organization.workspace.create({ name }, { query: { organizationId } })
    );
  }
).addAction(
  "update",
  async ({
    organizationId,
    workspaceId,
    name,
    slug,
    imageFile
  }) => {
    let res = await withOrganizationClient(
      (c) => c.organization.workspace.update(
        { name, slug, imageFile, workspaceId },
        { query: { organizationId } }
      )
    );
    workspaceBootState.$$refetchAll$$();
    return res;
  }
).build();
organizations.retrievers("getAll").load({ args: [] });
var useWorkspaces = () => useListRetriever9(workspaces, "getAll");
var useWorkspace = (workspaceId) => {
  let workspaces2 = useWorkspaces();
  return {
    ...workspaces2,
    items: void 0,
    data: workspaces2.items.find((i) => i.id == workspaceId || i.slug == workspaceId)
  };
};
var useCurrentWorkspace = () => {
  let params = useParams6();
  let workspaceId = params.workspaceId;
  return useWorkspace(workspaceId);
};
var useCreateWorkspace = () => {
  return useMutation8(useState8(() => workspaces.actions("create"))[0]);
};
var useUpdateWorkspace = () => {
  return useMutation8(useState8(() => workspaces.actions("update"))[0]);
};
var workspaceBootState = createLoader3({
  name: "workspaceBoot",
  hash: (i) => `workspaceBoot:${i.workspaceId}:${i.product ?? getGlobalShellConfigValue()?.product.id}`,
  fetch: async (i) => await (await organizationClient)().organization.workspace.boot({
    workspaceId: i.workspaceId,
    product: i.product ?? (await getGlobalShellConfig()).product.id
  }),
  mutators: {
    createProject: async ({ name }, { output: { workspace, organization, productWorkspace } }) => {
      let res = await (await organizationClient)().organization.workspace.project.create(
        { name, workspaceId: workspace.id, product: productWorkspace.product },
        {
          query: { organizationId: organization.id }
        }
      );
      workspaces.retrievers("getAll").load({ args: [], force: true });
      workspaceBootState.refetchAll();
      return res;
    },
    updateProject: async ({ projectId, name, slug }, { output: { workspace, organization } }) => {
      let res = await (await organizationClient)().organization.workspace.project.update(
        { name, slug, projectId, workspaceId: workspace.id },
        {
          query: { organizationId: organization.id }
        }
      );
      workspaces.retrievers("getAll").load({ args: [], force: true });
      workspaceBootState.refetchAll();
      return res;
    },
    createInstance: async ({
      name,
      type,
      projectId
    }, { output: { workspace, organization }, input }) => {
      let res = await (await organizationClient)().organization.workspace.project.createInstance(
        { name, type, projectId, workspaceId: workspace.id },
        {
          query: { organizationId: organization.id }
        }
      );
      workspaces.retrievers("getAll").load({ args: [], force: true });
      workspaceBootState.refetchAll();
      return res;
    },
    updateInstance: async ({ instanceId, name }) => {
      let res = await (await instanceClient)().instance.update({ name }, { query: { instanceId } });
      workspaces.retrievers("getAll").load({ args: [], force: true });
      workspaceBootState.refetchAll();
      return res;
    }
  }
});
var bootWorkspace = workspaceBootState.fetchAndReturn;
var useBootWorkspace = (opts) => {
  let res = workspaceBootState.use(opts);
  useEffect2(() => {
    if (res.error) {
      toast.error(res.error.data.message);
    }
  }, [res.error]);
  return res;
};
var useCurrentBootWorkspace = () => {
  let instance = useCurrentInstance();
  return useBootWorkspace(instance.data ? { workspaceId: instance.data.workspace.id } : null);
};
var useInstance = (instanceId) => {
  let org = useCurrentOrganization();
  let ctx = useShell();
  let project = useMemo9(() => {
    if (!org.data)
      return null;
    return org.data.projects.find(
      (p) => p.instances.some((i) => i.id == instanceId || i.slug == instanceId)
    );
  }, [org.data, instanceId]);
  let instance = useMemo9(() => {
    if (!project)
      return null;
    return project.instances.find((i) => i.id == instanceId || i.slug == instanceId);
  }, [project, instanceId]);
  let workspace = useMemo9(() => {
    if (!project)
      return null;
    return org.data?.workspaces.find((w) => w.id == project.workspaceId);
  }, [project, org.data]);
  let productWorkspace = useMemo9(() => {
    if (!workspace)
      return null;
    return workspace.productWorkspaces.find((pw) => pw.product == ctx.product.id);
  }, [workspace, ctx.product.id]);
  useBootWorkspace(project ? { workspaceId: project.workspaceId } : null);
  let organization = org.data?.organization;
  let error = org.error;
  if (!org.isLoading && org.data && !org && (!project || !instance || !workspace || !productWorkspace)) {
    error = new ServiceError3(notFoundError2({ entity: "instance" }));
  }
  return {
    isLoading: org.isLoading,
    error,
    data: project && instance && workspace && productWorkspace && organization ? {
      instance,
      project,
      workspace,
      productWorkspace,
      organization
    } : null
  };
};
var useCurrentInstance = () => {
  let { instanceId } = useParams6();
  return useInstance(instanceId);
};

// src/state/billing/billingObjectEntitlements.ts
var billingObjectEntitlementsState = createLoader4({
  name: "billingObjectEntitlements",
  hash: (i) => `billingObjectEntitlements:${i.organizationId}:${i.object.type}:${i.object.id}`,
  fetch: async (i) => (await billingClient)().billing.getEntitlements(
    { object: i.object },
    { query: { organizationId: i.organizationId } }
  ),
  mutators: {}
});
var useBillingObjectEntitlements = (input) => {
  return billingObjectEntitlementsState.use(input);
};
var useCurrentWorkspaceEntitlements = () => {
  let ws = useCurrentBootWorkspace();
  let ent = useBillingObjectEntitlements(
    ws.data?.productWorkspace ? {
      organizationId: ws.data.organization.id,
      object: { type: "workspace_product", id: ws.data.productWorkspace.id }
    } : null
  );
  return ent;
};

// src/state/billing/billingPricingTable.ts
import { createLoader as createLoader5 } from "@lb-lib/ui-data";
var billingPricingTableState = createLoader5({
  name: "billingPricingTable",
  hash: (i) => `billingPricingTable:${i.organizationId}:${i.object.type}:${i.object.id}:${i.pricingTableId}`,
  fetch: async (i) => (await billingClient)().billing.getPricingTable(
    { object: i.object, pricingTableId: i.pricingTableId },
    { query: { organizationId: i.organizationId } }
  ),
  mutators: {}
});
var useBillingPricingTable = (input) => {
  return billingPricingTableState.use(input);
};

// src/state/billing/fullBillingObject.ts
import { createLoader as createLoader6 } from "@lb-lib/ui-data";
var fullBillingObjectState = createLoader6({
  name: "fullBillingObject",
  hash: (i) => `fullBillingObject:${i.organizationId}:${i.object.id}:${i.object.type}`,
  fetch: async (i) => (await billingClient)().billing.getBillingObject(
    { object: i.object },
    { query: { organizationId: i.organizationId } }
  ),
  mutators: {
    setPlan: async (i, { input }) => withBillingClient(
      (client) => client.billing.setPlan(
        {
          planId: i.planId,
          interval: i.interval,
          object: input.object
        },
        { query: { organizationId: input.organizationId } }
      )
    ),
    getInvoicePdf: async (i, { input }) => withBillingClient(
      (client) => client.billing.getInvoicePdf(
        {
          transactionId: i.transactionId
        },
        { query: { organizationId: input.organizationId } }
      )
    ),
    updateObjectName: async (i, { input }) => withBillingClient(
      (client) => client.billing.updateObjectName(
        {
          name: i.name,
          object: input.object
        },
        { query: { organizationId: input.organizationId } }
      )
    ),
    changeSubscriptionPlan: async (i, { input }) => withBillingClient(
      (client) => client.billing.changeSubscriptionPlan(
        {
          planId: i.planId,
          interval: i.interval,
          subscriptionId: i.subscriptionId
        },
        { query: { organizationId: input.organizationId } }
      )
    ),
    cancelSubscription: async (i, { input }) => withBillingClient(
      (client) => client.billing.cancelSubscription(
        {
          subscriptionId: i.subscriptionId
        },
        { query: { organizationId: input.organizationId } }
      )
    ),
    resumeSubscription: async (i, { input }) => withBillingClient(
      (client) => client.billing.resumeSubscription(
        {
          subscriptionId: i.subscriptionId
        },
        { query: { organizationId: input.organizationId } }
      )
    ),
    cancelPendingChanges: async (i, { input }) => withBillingClient(
      (client) => client.billing.cancelPendingChanges(
        {
          subscriptionId: i.subscriptionId
        },
        { query: { organizationId: input.organizationId } }
      )
    ),
    updatePaymentMethod: async (i, { input }) => withBillingClient(
      (client) => client.billing.updatePaymentMethod(
        { subscriptionId: i.subscriptionId },
        { query: { organizationId: input.organizationId } }
      )
    )
  }
});
var useFullBillingObject = (organizationId, object) => {
  return fullBillingObjectState.use({ organizationId, object });
};

// src/state/dashboard/dashboardClient.ts
import { isServiceError as isServiceError5 } from "@lb-lib/error";
import { createClient as createClient4 } from "@lb-lib/rpc/client.mjs";
var dashboardClient = (async () => {
  let config = await getGlobalShellConfig();
  return () => createClient4({
    endpoint: config.services.coreApi.dashboardUrl,
    client: {
      name: config.product.id,
      version: "latest"
    },
    headers: {
      "loopbound-service": "dashboard",
      "loopbound-product": config.product.id
    },
    getHeaders: async () => {
      let token = await getToken();
      return {
        Authorization: `Bearer ${token}`
      };
    }
  });
})();
var withDashboardClient = async (fn) => {
  try {
    let client = await dashboardClient;
    return await fn(client());
  } catch (e) {
    if (isServiceError5(e) && e.data.status == 401)
      probeAuth();
    throw e;
  }
};

// src/state/dashboard/dashboardComment.ts
import { Resource as Resource11, useListRetriever as useListRetriever10, useMutation as useMutation10 } from "@lb-lib/ui-data";
import { useMemo as useMemo11, useState as useState10 } from "react";

// src/state/dashboard/dashboardObject.ts
import { base62 } from "@lb-lib/base62";
import { Resource as Resource10, useItemRetriever as useItemRetriever4, useMutation as useMutation9 } from "@lb-lib/ui-data";
import { useMemo as useMemo10, useState as useState9 } from "react";
var getDashboardObjectId = (input) => {
  let content = base62.encode(
    JSON.stringify({
      type: input.type,
      id: input.id,
      instanceId: input.instanceId
    })
  );
  return `doid_${content}`;
};
var dashboardObject = new Resource10("dashboardObject").addRetriever(
  "get",
  async (input) => {
    return await withDashboardClient(
      (c) => c.object.get(
        {
          objectId: getDashboardObjectId({
            id: input.object.id,
            type: input.object.type,
            instanceId: input.instanceId
          })
        },
        { query: { instanceId: input.instanceId } }
      )
    );
  }
).addAction(
  "update",
  async (input) => {
    return await withDashboardClient(
      (c) => c.object.update(
        {
          objectId: getDashboardObjectId({
            id: input.object.id,
            type: input.object.type,
            instanceId: input.instanceId
          }),
          name: input.name,
          description: input.description
        },
        { query: { instanceId: input.instanceId } }
      )
    );
  }
).build();
var useDashboardObjectFull = (opts) => useItemRetriever4(
  dashboardObject,
  "get",
  useMemo10(() => opts, [opts?.instanceId, opts?.object.id, opts?.object.type])
);
var useDashboardObject = (object) => {
  let instance = useCurrentInstance();
  return useDashboardObjectFull(
    instance.data?.instance.id ? {
      instanceId: instance.data.instance.id,
      object
    } : null
  );
};
var useUpdateDashboardObject = () => {
  return useMutation9(useState9(() => dashboardObject.actions("update"))[0]);
};

// src/state/dashboard/dashboardComment.ts
var dashboardComment = new Resource11("dashboardComment").addRetriever(
  "list",
  async (input) => {
    return await withDashboardClient(
      (c) => c.object.comments.list(
        {
          objectId: getDashboardObjectId({
            id: input.object.id,
            type: input.object.type,
            instanceId: input.instanceId
          })
        },
        { query: { instanceId: input.instanceId } }
      )
    );
  }
).addAction(
  "create",
  async (input) => {
    return await withDashboardClient(
      (c) => c.object.comments.create(
        {
          objectId: getDashboardObjectId({
            id: input.object.id,
            type: input.object.type,
            instanceId: input.instanceId
          }),
          content: input.content
        },
        { query: { instanceId: input.instanceId } }
      )
    );
  }
).addAction(
  "update",
  async (input) => {
    return await withDashboardClient(
      (c) => c.object.comments.update(
        {
          objectId: getDashboardObjectId({
            id: input.object.id,
            type: input.object.type,
            instanceId: input.instanceId
          }),
          commentId: input.commentId,
          content: input.content
        },
        { query: { instanceId: input.instanceId } }
      )
    );
  }
).addAction(
  "delete",
  async (input) => {
    return await withDashboardClient(
      (c) => c.object.comments.delete(
        {
          objectId: getDashboardObjectId({
            id: input.object.id,
            type: input.object.type,
            instanceId: input.instanceId
          }),
          commentId: input.commentId
        },
        { query: { instanceId: input.instanceId } }
      )
    );
  }
).build();
var useDashboardCommentsFull = (opts) => useListRetriever10(
  dashboardComment,
  "list",
  useMemo11(() => opts, [opts?.instanceId, opts?.object.id, opts?.object.type])
);
var useDashboardComments = (object) => {
  let instance = useCurrentInstance();
  return useDashboardCommentsFull(
    instance.data?.instance.id ? {
      instanceId: instance.data.instance.id,
      object
    } : null
  );
};
var useCreateDashboardComment = () => {
  return useMutation10(useState10(() => dashboardComment.actions("create"))[0]);
};
var useUpdateDashboardComment = () => {
  return useMutation10(useState10(() => dashboardComment.actions("update"))[0]);
};
var useDeleteDashboardComment = () => {
  return useMutation10(useState10(() => dashboardComment.actions("delete"))[0]);
};

// src/state/dashboard/dashboardOnboarding.ts
import { Resource as Resource12, useItemRetriever as useItemRetriever5, useMutation as useMutation11 } from "@lb-lib/ui-data";
import { useMemo as useMemo12, useState as useState11 } from "react";
var onboardingGroup = new Resource12("onboardingGroup").addRetriever(
  "get",
  async (input) => {
    return await withDashboardClient(
      (c) => c.onboarding.ensureGroup(
        {
          onboardingGroupKey: input.onboardingGroupKey,
          tasks: input.tasks
        },
        { query: { instanceId: input.instanceId } }
      )
    );
  }
).addSubAction("skip", async (input) => {
  return await withDashboardClient(
    (c) => c.onboarding.skipGroup(
      { onboardingGroupKey: input.onboardingGroupKey },
      { query: { instanceId: input.instanceId } }
    )
  );
}).addSubAction(
  "complete",
  async (input) => {
    return await withDashboardClient(
      (c) => c.onboarding.task.complete(
        {
          onboardingGroupKey: input.onboardingGroupKey,
          onboardingTaskKey: input.onboardingTaskKey
        },
        { query: { instanceId: input.instanceId } }
      )
    );
  }
).addSubAction(
  "dismiss",
  async (input) => {
    return await withDashboardClient(
      (c) => c.onboarding.task.dismiss(
        {
          onboardingGroupKey: input.onboardingGroupKey,
          onboardingTaskKey: input.onboardingTaskKey
        },
        { query: { instanceId: input.instanceId } }
      )
    );
  }
).addSubAction(
  "dismissAll",
  async (input) => {
    return await withDashboardClient(
      (c) => c.onboarding.task.dismissAll(
        { onboardingGroupKey: input.onboardingGroupKey },
        { query: { instanceId: input.instanceId } }
      )
    );
  }
).build();
var useOnboardingGroupFull = (opts) => useItemRetriever5(
  onboardingGroup,
  "get",
  useMemo12(() => opts, [opts?.instanceId, opts?.onboardingGroupKey, opts?.tasks])
);
var useOnboardingGroup = (opts) => {
  let instance = useCurrentInstance();
  return useOnboardingGroupFull(
    instance.data?.instance.id && opts ? {
      instanceId: instance.data.instance.id,
      ...opts
    } : null
  );
};
var useCompleteOnboardingTask = () => {
  return useMutation11(useState11(() => onboardingGroup.subActions("complete"))[0]);
};
var useDismissOnboardingTask = () => {
  return useMutation11(useState11(() => onboardingGroup.subActions("dismiss"))[0]);
};
var useDismissAllOnboardingTasks = () => {
  return useMutation11(useState11(() => onboardingGroup.subActions("dismissAll"))[0]);
};
var useSkipOnboardingGroup = () => {
  return useMutation11(useState11(() => onboardingGroup.subActions("skip"))[0]);
};

// src/state/dashboard/dashboardPage.ts
import { Resource as Resource13, useItemRetriever as useItemRetriever6, useMutation as useMutation12 } from "@lb-lib/ui-data";
import { useMemo as useMemo13, useState as useState12 } from "react";
var dashboardPage = new Resource13("dashboardPage").addRetriever("get", async (input) => {
  return await withDashboardClient(
    (c) => c.page.get({ pageKey: input.pageKey }, { query: { instanceId: input.instanceId } })
  );
}).addAction(
  "update",
  async (input) => {
    return await withDashboardClient(
      (c) => c.page.update(
        {
          pageKey: input.pageKey,
          name: input.name,
          description: input.description
        },
        { query: { instanceId: input.instanceId } }
      )
    );
  }
).addSubAction(
  "createGroup",
  async (input) => {
    return await withDashboardClient(
      (c) => c.page.objectGroup.create(
        {
          pageKey: input.pageKey,
          name: input.name,
          description: input.description
        },
        { query: { instanceId: input.instanceId } }
      )
    );
  }
).addSubAction(
  "updateGroup",
  async (input) => {
    return await withDashboardClient(
      (c) => c.page.objectGroup.update(
        {
          pageKey: input.pageKey,
          objectGroupId: input.objectGroupId,
          name: input.name,
          description: input.description
        },
        { query: { instanceId: input.instanceId } }
      )
    );
  }
).addSubAction(
  "archiveGroup",
  async (input) => {
    return await withDashboardClient(
      (c) => c.page.objectGroup.archive(
        {
          pageKey: input.pageKey,
          objectGroupId: input.objectGroupId
        },
        { query: { instanceId: input.instanceId } }
      )
    );
  }
).addAction(
  "changeOrder",
  async (input) => {
    return await withDashboardClient(
      (c) => c.page.objectGroup.order(
        {
          pageKey: input.pageKey,
          order: input.order
        },
        { query: { instanceId: input.instanceId } }
      )
    );
  }
).build();
var useDashboardPageFull = (opts) => useItemRetriever6(
  dashboardPage,
  "get",
  useMemo13(() => opts, [opts?.instanceId, opts?.pageKey])
);
var useDashboardPage = (pageKey) => {
  let instance = useCurrentInstance();
  return useDashboardPageFull(
    instance.data?.instance.id ? {
      instanceId: instance.data.instance.id,
      pageKey
    } : null
  );
};
var useCreateDashboardPageObjectGroup = () => {
  return useMutation12(useState12(() => dashboardPage.subActions("createGroup"))[0]);
};
var useUpdateDashboardPageObjectGroup = () => {
  return useMutation12(useState12(() => dashboardPage.subActions("updateGroup"))[0]);
};
var useArchiveDashboardPageObjectGroup = () => {
  return useMutation12(useState12(() => dashboardPage.subActions("archiveGroup"))[0]);
};
var useOrderDashboardPageObjectGroup = () => {
  return useMutation12(useState12(() => dashboardPage.actions("changeOrder"))[0]);
};
var useUpdateDashboardPage = () => {
  return useMutation12(useState12(() => dashboardPage.actions("update"))[0]);
};

// src/state/dashboard/dashboardPageList.ts
import { Resource as Resource14, useItemRetriever as useItemRetriever7, useMutation as useMutation13 } from "@lb-lib/ui-data";
import { useMemo as useMemo14, useState as useState13 } from "react";
var dashboardPageList = new Resource14("dashboardPageList").addRetriever(
  "get",
  async (input) => {
    return await withDashboardClient(
      (c) => c.page.pageList.get(input, { query: { instanceId: input.instanceId } })
    );
  }
).addSubAction(
  "upsertPage",
  async (input) => {
    return await withDashboardClient(
      (c) => c.page.pageList.upsertPage(
        {
          pageKey: input.pageKey,
          name: input.name,
          pageListKey: input.pageListKey,
          description: input.description
        },
        { query: { instanceId: input.instanceId } }
      )
    );
  }
).build();
var useDashboardPageListFull = (opts) => useItemRetriever7(
  dashboardPageList,
  "get",
  useMemo14(() => opts, [opts?.instanceId, opts?.pageListKey])
);
var useDashboardPageList = (pageListKey, pages) => {
  let instance = useCurrentInstance();
  return useDashboardPageListFull(
    instance.data?.instance.id ? {
      instanceId: instance.data.instance.id,
      pageListKey,
      pages
    } : null
  );
};
var useUpsertDashboardPageListPage = () => {
  let res = useMutation13(useState13(() => dashboardPageList.subActions("upsertPage"))[0]);
  return res;
};

// src/state/developer/apiKey.ts
import { Resource as Resource15, useItemRetriever as useItemRetriever8, useListRetriever as useListRetriever11, useMutation as useMutation14 } from "@lb-lib/ui-data";
import { useMemo as useMemo15, useState as useState14 } from "react";

// src/state/developer/developersClient.ts
import { createClient as createClient5 } from "@lb-lib/rpc/client.mjs";
var developerClient = (async () => {
  let config = await getGlobalShellConfig();
  return () => createClient5({
    endpoint: config.services.coreApi.developerUrl,
    client: {
      name: config.product.id,
      version: "latest"
    },
    headers: {
      "loopbound-service": "developer",
      "loopbound-product": config.product.id
    },
    getHeaders: async () => {
      let token = await getToken();
      return {
        Authorization: `Bearer ${token}`
      };
    }
  });
})();
var withDeveloperClient = async (fn) => {
  let client = await developerClient;
  return await fn(client());
};

// src/state/developer/apiKey.ts
var apiKeys = new Resource15("apiKeys").addRetriever(
  "list",
  async ({
    applicationId,
    organizationId,
    types,
    cursor,
    filter,
    limit
  }) => {
    return await withDeveloperClient(
      (c) => c.apiKey.list(
        { applicationId, cursor, limit, types, ...filter },
        { query: { organizationId } }
      )
    );
  }
).addRetriever(
  "get",
  async ({ organizationId, apiKeyId }) => {
    return await withDeveloperClient(
      (c) => c.apiKey.get({ apiKeyId }, { query: { organizationId } })
    );
  }
).addAction(
  "create",
  async ({
    applicationId,
    organizationId,
    type,
    name,
    description,
    expiresAt
  }) => {
    return await withDeveloperClient(
      (c) => c.apiKey.create(
        { applicationId, type, name, description, expiresAt },
        { query: { organizationId } }
      )
    );
  }
).addAction(
  "update",
  async ({
    organizationId,
    apiKeyId,
    name,
    description,
    expiresAt
  }) => {
    return await withDeveloperClient(
      (c) => c.apiKey.update(
        { apiKeyId, name, description, expiresAt },
        { query: { organizationId } }
      )
    );
  }
).addAction(
  "delete",
  async ({ organizationId, apiKeyId }) => {
    return await withDeveloperClient(
      (c) => c.apiKey.delete({ apiKeyId }, { query: { organizationId } })
    );
  }
).addAction(
  "roll",
  async ({
    organizationId,
    apiKeyId,
    remainingSecondsLeft
  }) => {
    let { newApiKey } = await withDeveloperClient(
      (c) => c.apiKey.roll({ apiKeyId, remainingSecondsLeft }, { query: { organizationId } })
    );
    return newApiKey;
  }
).addSubAction(
  "reveal",
  async ({ apiKeyId, organizationId }) => {
    return await withDeveloperClient(
      (c) => c.apiKey.reveal({ apiKeyId }, { query: { organizationId } })
    );
  }
).build();
var useApiKeys = (opts) => useListRetriever11(
  apiKeys,
  "list",
  useMemo15(
    () => opts,
    [
      opts.applicationId,
      opts.organizationId,
      opts.filter,
      opts.types,
      opts.limit,
      opts.cursor
    ]
  )
);
var useApiKey = (opts) => useItemRetriever8(
  apiKeys,
  "get",
  useMemo15(() => opts, [opts?.organizationId, opts?.apiKeyId])
);
var useCreateApiKey = () => {
  return useMutation14(useState14(() => apiKeys.actions("create"))[0]);
};
var useUpdateApiKey = () => {
  return useMutation14(useState14(() => apiKeys.actions("update"))[0]);
};
var useDeleteApiKey = () => {
  return useMutation14(useState14(() => apiKeys.actions("delete"))[0]);
};
var useRollApiKey = () => {
  return useMutation14(useState14(() => apiKeys.actions("roll"))[0]);
};
var revealedApiKey = {};
var getCachedRevealedApiKey = (apiKeyId) => {
  let cached = revealedApiKey[apiKeyId];
  if (!cached)
    return;
  if (Date.now() - cached.ts > 1e3 * 60 * 30) {
    delete revealedApiKey[apiKeyId];
    return;
  }
  return cached;
};
var useRevealableApiKey = ({
  organizationId,
  apiKeyId
}) => {
  let [value, setValue] = useState14(
    () => getCachedRevealedApiKey(apiKeyId)?.secret
  );
  let doReveal = useMutation14(useState14(() => apiKeys.subActions("reveal"))[0]);
  let reveal = () => {
    let cached = getCachedRevealedApiKey(apiKeyId);
    if (cached) {
      setValue(cached.secret);
      return;
    }
    doReveal.mutate({ apiKeyId, organizationId }).then(async (res) => {
      if (!res)
        return;
      revealedApiKey[apiKeyId] = {
        secret: res.secret,
        ts: Date.now()
      };
      setValue(res?.secret);
    });
  };
  let hide = () => {
    setValue(void 0);
  };
  return {
    value,
    isLoading: doReveal.isLoading,
    reveal,
    hide
  };
};
var revealSecret = async (opts) => {
  let res = await apiKeys.subActions("reveal")({
    apiKeyId: opts.apiKeyId,
    organizationId: opts.organizationId
  });
  return res.secret;
};

// src/state/developer/application.ts
import { Resource as Resource16, useItemRetriever as useItemRetriever9, useListRetriever as useListRetriever12, useMutation as useMutation15 } from "@lb-lib/ui-data";
import { useMemo as useMemo16, useState as useState15 } from "react";
var applications = new Resource16("applications").addRetriever(
  "list",
  async ({
    instanceIds,
    organizationId,
    cursor,
    filter
  }) => {
    return await withDeveloperClient(
      (c) => c.application.list({ instanceIds, cursor, ...filter }, { query: { organizationId } })
    );
  }
).addRetriever(
  "get",
  async ({
    applicationId,
    organizationId
  }) => {
    return await withDeveloperClient(
      (c) => c.application.get({ applicationId }, { query: { organizationId } })
    );
  }
).addAction(
  "create",
  async ({
    name,
    description,
    instanceId
  }) => {
    return await withDeveloperClient(
      (c) => c.application.create({ name, description }, { query: { instanceId } })
    );
  }
).addAction(
  "update",
  async ({
    organizationId,
    applicationId,
    name,
    description
  }) => {
    return await withDeveloperClient(
      (c) => c.application.update(
        { name, description, applicationId },
        { query: { organizationId } }
      )
    );
  }
).addAction(
  "delete",
  async ({
    organizationId,
    applicationId
  }) => {
    return await withDeveloperClient(
      (c) => c.application.delete({ applicationId }, { query: { organizationId } })
    );
  }
).build();
var useApplications = (opts) => useListRetriever12(
  applications,
  "list",
  useMemo16(() => opts, [opts?.instanceIds.join(","), opts?.organizationId, opts?.filter])
);
var useApplication = (opts) => useItemRetriever9(
  applications,
  "get",
  useMemo16(() => opts, [opts?.applicationId, opts?.organizationId])
);
var useCreateApplication = () => {
  return useMutation15(useState15(() => applications.actions("create"))[0]);
};
var useUpdateApplication = () => {
  return useMutation15(useState15(() => applications.actions("update"))[0]);
};
var useDeleteApplication = () => {
  return useMutation15(useState15(() => applications.actions("delete"))[0]);
};

// src/state/developer/applicationActivity.ts
import { Resource as Resource17, useListRetriever as useListRetriever13 } from "@lb-lib/ui-data";
import { useMemo as useMemo17 } from "react";
var applicationActivities = new Resource17("applicationActivities").addRetriever(
  "list",
  async ({
    applicationId,
    organizationId,
    cursor,
    filter
  }) => {
    return await withDeveloperClient(
      (c) => c.application.activity(
        { applicationId, cursor, ...filter },
        { query: { organizationId } }
      )
    );
  }
).build();
var useMachineAccessActivities = (opts) => useListRetriever13(
  applicationActivities,
  "list",
  useMemo17(() => opts, [opts.applicationId, opts.organizationId, opts.filter, opts.cursor])
);

// src/state/developer/event.ts
import { Resource as Resource18, useItemRetriever as useItemRetriever10, useListRetriever as useListRetriever14 } from "@lb-lib/ui-data";
import { useMemo as useMemo18 } from "react";
var events = new Resource18("events").addRetriever(
  "list",
  async ({
    instanceId,
    organizationId,
    cursor,
    filter,
    objectIds
  }) => {
    return await withDeveloperClient(
      (c) => c.event.list(
        { instanceId, cursor, objectIds, order: "desc", ...filter },
        { query: { organizationId } }
      )
    );
  }
).build();
var useEvents = (opts) => useListRetriever14(
  events,
  "list",
  useMemo18(
    () => opts,
    [opts?.instanceId, opts?.organizationId, opts?.filter, opts?.objectIds?.join(",")]
  )
);
var fullEvent = new Resource18("event_full").addRetriever(
  "get",
  async ({
    instanceId,
    organizationId,
    eventId
  }) => {
    return await withDeveloperClient(
      (c) => c.event.get({ eventId }, { query: { organizationId, instanceId } })
    );
  }
).build();
var useEvent = (opts) => useItemRetriever10(
  fullEvent,
  "get",
  useMemo18(() => opts, [opts.instanceId, opts.organizationId, opts.eventId])
);

// src/state/developer/request.ts
import { createLoader as createLoader7, Resource as Resource19, useItemRetriever as useItemRetriever11, useListRetriever as useListRetriever15 } from "@lb-lib/ui-data";
import { useMemo as useMemo19 } from "react";
var requests = new Resource19("requests").addRetriever(
  "list",
  async ({
    instanceId,
    organizationId,
    cursor,
    filter,
    errorId
  }) => {
    return await withDeveloperClient(
      (c) => c.request.list(
        { instanceId, cursor, errorId, order: "desc", ...filter },
        { query: { organizationId } }
      )
    );
  }
).build();
var useRequests = (opts) => useListRetriever15(
  requests,
  "list",
  useMemo19(() => opts, [opts?.instanceId, opts?.organizationId, opts?.filter, opts?.errorId])
);
var fullRequest = new Resource19("request_full").addRetriever(
  "get",
  async ({
    instanceId,
    organizationId,
    requestId
  }) => {
    return await withDeveloperClient(
      (c) => c.request.get({ requestId }, { query: { organizationId, instanceId } })
    );
  }
).build();
var useRequest = (opts) => useItemRetriever11(
  fullRequest,
  "get",
  useMemo19(() => opts, [opts?.instanceId, opts?.organizationId, opts?.requestId])
);
var apiErrors = new Resource19("api_errors").addRetriever(
  "list",
  async ({ instanceId, organizationId }) => {
    return await withDeveloperClient(
      (c) => c.request.listRecentErrors({ instanceId }, { query: { organizationId } })
    );
  }
).build();
var useApiErrors = (opts) => useListRetriever15(
  apiErrors,
  "list",
  useMemo19(() => opts, [opts?.instanceId, opts?.organizationId])
);
var requestTimeline = createLoader7({
  name: "requests_timeline",
  hash: ({ instanceId, organizationId, intervalMinutes, interval }) => `${instanceId}-${organizationId}-${intervalMinutes}-${interval.from.toISOString()}-${interval.to.toISOString()}`,
  fetch: async (input) => {
    return await withDeveloperClient(
      (c) => c.request.timeline(input, {
        query: { organizationId: input.organizationId, instanceId: input.instanceId }
      })
    );
  },
  mutators: {}
});
var useRequestTimeline = requestTimeline.use;

// src/state/file/file.ts
import { ServiceError as ServiceError4, internalServerError as internalServerError2 } from "@lb-lib/error";
import { Resource as Resource20, useMutation as useMutation16 } from "@lb-lib/ui-data";
import { useState as useState16 } from "react";

// src/state/file/fileClient.ts
import { isServiceError as isServiceError6 } from "@lb-lib/error";
import { createClient as createClient6 } from "@lb-lib/rpc/client.mjs";
var fileClient = (async () => {
  let config = await getGlobalShellConfig();
  return () => createClient6({
    endpoint: config.services.coreApi.fileUrl,
    client: {
      name: config.product.id,
      version: "latest"
    },
    headers: {
      "loopbound-service": "file",
      "loopbound-product": config.product.id
    },
    getHeaders: async () => {
      let token = await getToken();
      return {
        Authorization: `Bearer ${token}`
      };
    }
  });
})();
var withFileClient = async (fn) => {
  try {
    let client = await fileClient;
    return await fn(client());
  } catch (e) {
    if (isServiceError6(e) && e.data.status == 401)
      probeAuth();
    throw e;
  }
};

// src/state/file/file.ts
var files = new Resource20("files").addRetriever(
  "get",
  async ({ organizationId, fileId }) => {
    return await withFileClient(
      (c) => c.file.get({ fileId }, { query: { organizationId } })
    );
  }
).addAction(
  "create",
  async (input) => {
    if (!input.file && !input.url)
      throw new Error("file or url is required");
    let res = await withFileClient(
      (c) => c.file.createFile(
        {
          type: input.type,
          instanceId: input.type == "instance_file" ? input.instanceId : void 0,
          fileType: input.type == "instance_file" ? input.fileType : void 0,
          file: input.file ? {
            type: "upload",
            name: input.name,
            mime: input.file.type,
            size: input.file.size
          } : {
            type: "external",
            name: input.name,
            url: input.url
          }
        },
        { query: { organizationId: input.organizationId } }
      )
    );
    if (input.url)
      return res.file;
    try {
      await fetch(res.uploadUrl, {
        method: "PUT",
        body: input.file
      });
    } catch (e) {
      console.error("Failed to upload file", e);
      throw new ServiceError4(
        internalServerError2({
          message: "Failed to upload file"
        })
      );
    }
    res.file = await withFileClient(
      (c) => c.file.completeFileUpload(
        { fileId: res.file.id },
        { query: { organizationId: input.organizationId } }
      )
    );
    return res.file;
  }
).addSubAction("create-link", (input) => {
  return withFileClient(
    (c) => c.file.createLink({
      fileId: input.fileId,
      expiresAt: input.expiresAt
    })
  );
}).build();
var useCreateFile = () => {
  return useMutation16(useState16(() => files.actions("create"))[0]);
};
var createFile = async (input) => {
  return await files.actions("create")({
    ...input,
    fileType: input.type,
    type: "instance_file"
  });
};
var createFileLink = async (file, opts) => {
  return await files.subActions("create-link")({
    fileId: file.id,
    expiresAt: opts?.expiresAt
  });
};
var uploadFile = createFile;

// src/state/user/user.ts
import { createLoader as createLoader8 } from "@lb-lib/ui-data";

// src/state/user/userClient.ts
import { isServiceError as isServiceError7 } from "@lb-lib/error";
import { createClient as createClient7 } from "@lb-lib/rpc/client.mjs";
var userClient = (async () => {
  let config = await getGlobalShellConfig();
  return () => createClient7({
    endpoint: config.services.coreApi.userUrl,
    client: {
      name: config.product.id,
      version: "latest"
    },
    headers: {
      "loopbound-service": "user",
      "loopbound-product": config.product.id
    },
    getHeaders: async () => {
      let token = await getToken();
      return {
        Authorization: `Bearer ${token}`
      };
    }
  });
})();
var withUserClient = async (fn) => {
  try {
    let client = await userClient;
    return await fn(client());
  } catch (e) {
    if (isServiceError7(e) && e.data.status == 401)
      probeAuth();
    throw e;
  }
};

// src/state/user/user.ts
var userState = createLoader8({
  name: "user",
  hash: () => "user",
  // There is only one user state
  fetch: async () => (await userClient)().user.get({}),
  mutators: {
    update: async (d) => {
      let res = await (await userClient)().user.update(d);
      reloadSession();
      return res;
    }
  }
});
var useUser = () => {
  return userState.use();
};
userState.fetch();

// src/state/user/userActions.ts
import { Resource as Resource21, useListRetriever as useListRetriever16 } from "@lb-lib/ui-data";
var userActions = new Resource21("userActions").addRetriever("list", async (input) => {
  return await withUserClient(
    (c) => c.user.listActions({
      order: "desc",
      limit: 10,
      cursor: input?.cursor
    })
  );
}).build();
var useUserActions = () => useListRetriever16(userActions, "list");

// src/state/user/userEmail.ts
import { Resource as Resource22, useListRetriever as useListRetriever17, useMutation as useMutation17 } from "@lb-lib/ui-data";
import { useState as useState17 } from "react";
var emails = new Resource22("emails").addRetriever("getAll", async () => {
  return await withUserClient((c) => c.user.email.getAll({}));
}).addAction("add", async (input) => {
  return await withUserClient((c) => c.user.email.add(input));
}).addAction("remove", async (input) => {
  return await withUserClient((c) => c.user.email.remove(input));
}).addAction("setPrimary", async (input) => {
  return await withUserClient((c) => c.user.email.setPrimary(input));
}).build();
var useUserEmails = () => useListRetriever17(emails, "getAll");
var useAddEmail = () => {
  return useMutation17(useState17(() => emails.actions("add"))[0]);
};
var useRemoveEmail = () => {
  return useMutation17(useState17(() => emails.actions("remove"))[0]);
};
var useSetPrimaryEmail = () => {
  return useMutation17(useState17(() => emails.actions("setPrimary"))[0]);
};

// src/state/user/userProfile.ts
import { Resource as Resource23, useListRetriever as useListRetriever18 } from "@lb-lib/ui-data";
var profiles = new Resource23("profiles").addRetriever("getAll", async () => {
  return await withUserClient((c) => c.user.profile.getAll({}));
}).build();
var useUserProfiles = () => useListRetriever18(profiles, "getAll");

// src/state/user/userSession.ts
import { Resource as Resource24, useListRetriever as useListRetriever19, useMutation as useMutation18 } from "@lb-lib/ui-data";
import { useState as useState18 } from "react";
var sessions = new Resource24("sessions").addRetriever("getAll", async () => {
  return await withUserClient((c) => c.user.session.getAll({}));
}).addAction("logout", async (input) => {
  return await withUserClient((c) => c.user.session.logout(input));
}).build();
var useUserSessions = () => useListRetriever19(sessions, "getAll");
var useLogoutSession = () => {
  return useMutation18(useState18(() => sessions.actions("logout"))[0]);
};

// src/state/utils/countries.ts
import { createLoader as createLoader9 } from "@lb-lib/ui-data";

// src/state/utils/utilsClient.ts
import { isServiceError as isServiceError8 } from "@lb-lib/error";
import { createClient as createClient8 } from "@lb-lib/rpc/client.mjs";
var utilsClient = (async () => {
  let config = await getGlobalShellConfig();
  return () => createClient8({
    endpoint: config.services.coreApi.utilsUrl,
    client: {
      name: config.product.id,
      version: "latest"
    },
    headers: {
      "loopbound-service": "utils",
      "loopbound-product": config.product.id
    },
    getHeaders: async () => {
      let token = await getToken();
      return {
        Authorization: `Bearer ${token}`
      };
    }
  });
})();
var withUtilsClient = async (fn) => {
  try {
    let client = await utilsClient;
    return await fn(client());
  } catch (e) {
    if (isServiceError8(e) && e.data.status == 401)
      probeAuth();
    throw e;
  }
};

// src/state/utils/countries.ts
var countriesState = createLoader9({
  name: "countries",
  hash: () => "countries",
  // There is only one user state
  fetch: async () => (await utilsClient)().utils.getCountries({}),
  mutators: {}
});
var useCountries = () => {
  return countriesState.use();
};

// src/state/utils/machineContext.ts
import { createLoader as createLoader10 } from "@lb-lib/ui-data";
import { useMemo as useMemo20 } from "react";
var machineContextState = createLoader10({
  name: "machineContext",
  hash: (i) => i.machineContextId,
  fetch: async (input) => (await utilsClient)().utils.getMachineContext({
    machineContextId: input.machineContextId
  }),
  mutators: {}
});
var useMachineContext = (machineContextId) => {
  return machineContextState.use(useMemo20(() => ({ machineContextId }), [machineContextId]));
};

// src/state/utils/timezones.ts
import { createLoader as createLoader11 } from "@lb-lib/ui-data";
var timezonesState = createLoader11({
  name: "timezones",
  hash: () => "timezones",
  // There is only one user state
  fetch: async () => (await utilsClient)().utils.getTimezones({}),
  mutators: {}
});
var useTimezones = () => {
  return timezonesState.use();
};
export {
  ResolvablePromise,
  ShellProvider,
  apiKeys,
  applicationActivities,
  applications,
  billingAccountState,
  billingClient,
  billingObjectEntitlementsState,
  billingObjectState,
  billingPricingTableState,
  bootWorkspace,
  countriesState,
  createFile,
  createFileLink,
  dashboardClient,
  dashboardComment,
  dashboardObject,
  dashboardPage,
  dashboardPageList,
  developerClient,
  emails,
  events,
  fileClient,
  files,
  fullBillingObjectState,
  fullEvent,
  fullRequest,
  getDashboardObjectId,
  getGlobalShellConfig,
  getReferrer,
  getServiceUrl,
  getToken,
  getUserOptional,
  instanceClient,
  logout,
  machineContextState,
  onboardingGroup,
  organizationAlerts,
  organizationAuditLogs,
  organizationClient,
  organizationIntegrations,
  organizationInvites,
  organizationMembers,
  organizationNotifications,
  organizations,
  probeAuth,
  profiles,
  redirectToUserSelector,
  reloadSession,
  requests,
  revealSecret,
  sessions,
  supportTicketMessages,
  supportTickets,
  timezonesState,
  uploadFile,
  useAddEmail,
  useApiErrors,
  useApiKey,
  useApiKeys,
  useApplication,
  useApplications,
  useArchiveDashboardPageObjectGroup,
  useArchiveOrganizationNotification,
  useBillingAccount,
  useBillingObject,
  useBillingObjectEntitlements,
  useBillingPricingTable,
  useBootWorkspace,
  useCompleteOnboardingTask,
  useCountries,
  useCreateApiKey,
  useCreateApplication,
  useCreateDashboardComment,
  useCreateDashboardPageObjectGroup,
  useCreateFile,
  useCreateOrganization,
  useCreateOrganizationInvite,
  useCreateSupportTicket,
  useCreateSupportTicketAttachment,
  useCreateSupportTicketMessage,
  useCreateWorkspace,
  useCurrentBootWorkspace,
  useCurrentInstance,
  useCurrentOrganization,
  useCurrentOrganizationAlerts,
  useCurrentOrganizationAuditLogs,
  useCurrentOrganizationInvites,
  useCurrentOrganizationMembers,
  useCurrentWorkspace,
  useCurrentWorkspaceEntitlements,
  useDashboardComments,
  useDashboardCommentsFull,
  useDashboardObject,
  useDashboardObjectFull,
  useDashboardPage,
  useDashboardPageFull,
  useDashboardPageList,
  useDashboardPageListFull,
  useDeleteApiKey,
  useDeleteApplication,
  useDeleteDashboardComment,
  useDismissAllOnboardingTasks,
  useDismissOnboardingTask,
  useDismissOrganizationAlert,
  useEvent,
  useEvents,
  useFullBillingObject,
  useInstance,
  useLogoutSession,
  useMachineAccessActivities,
  useMachineContext,
  useOnboardingGroup,
  useOnboardingGroupFull,
  useOrderDashboardPageObjectGroup,
  useOrganization,
  useOrganizationAlerts,
  useOrganizationAuditLogs,
  useOrganizationIntegrations,
  useOrganizationInviteLink,
  useOrganizationInvites,
  useOrganizationMember,
  useOrganizationMembers,
  useOrganizationNotifications,
  useOrganizations,
  useReadOrganizationNotification,
  useRemoveEmail,
  useRemoveOrganizationInvite,
  useRemoveOrganizationMember,
  useRequest,
  useRequestTimeline,
  useRequests,
  useRevealableApiKey,
  useRollApiKey,
  useSession,
  useSetPrimaryEmail,
  useShell,
  useSkipOnboardingGroup,
  useSupportTicket,
  useSupportTicketMessages,
  useSupportTickets,
  useTimezones,
  useUnarchiveOrganizationNotification,
  useUninstallOrganizationIntegration,
  useUpdateApiKey,
  useUpdateApplication,
  useUpdateDashboardComment,
  useUpdateDashboardObject,
  useUpdateDashboardPage,
  useUpdateDashboardPageObjectGroup,
  useUpdateOrganization,
  useUpdateOrganizationMember,
  useUpdateWorkspace,
  useUpsertDashboardPageListPage,
  useUser,
  useUserActions,
  useUserEmails,
  useUserProfiles,
  useUserSessions,
  useWorkspace,
  useWorkspaces,
  userActions,
  userClient,
  userState,
  utilsClient,
  withBillingClient,
  withDashboardClient,
  withDeveloperClient,
  withFileClient,
  withInstanceClient,
  withOrganizationClient,
  withUserClient,
  withUtilsClient,
  workspaceBootState,
  workspaces
};
//# sourceMappingURL=index.mjs.map